import { forwardRef, type ReactNode } from 'react';
import { cn } from '@/utils/tailwind.util';
export interface DetailHeroCardProps {
  /** 데스크톱: 흰 카드 안 좌측 이미지 */
  image: ReactNode;
  /**
   * 모바일 이미지. 생략하면 `image` 를 그대로 쓴다.
   * 크기·여백이 데스크톱과 다른 경우(폴더 180×130 → 120×85) 따로 넘긴다.
   */
  mobileImage?: ReactNode;
  /** 모바일에서 이미지 우측에 붙는 것 (퀴즈의 맵 타입 Chip). */
  mobileImageAside?: ReactNode;
  /** 흰 카드 본문. 모바일·데스크톱이 같은 노드를 쓴다 — 타이포 반응형은 본문이 소유한다. */
  children: ReactNode;
  className?: string;
}

/**
 * 상세 화면 히어로 카드 셸.
 *
 * 모바일과 데스크톱이 **구조 자체가 다르다.** 반응형 클래스로 한 벌을 늘렸다 줄이는 게
 * 아니라 두 벌을 각각 렌더한다 — 모바일은 이미지가 흰 카드 **밖**으로 나가 배너 위에 뜨고,
 * 흰 카드에는 텍스트만 남기 때문이다.
 *
 * ```
 * 모바일 (<tablet-sm)              데스크톱 (>=tablet-sm)
 * ─────────────────────────        ────────────────────────────
 * flex-col gap-2xs                 흰 카드 하나 · flex items-start gap-xl
 * ├ 이미지 (카드 밖)                ├ 이미지
 * └ 흰 카드 p-md (텍스트만)         └ 본문
 * ```
 *
 * 원래 `pages/quiz/[id]/.../QuizDetailHeroCard` 안에만 있던 구조다. 폴더 상세가
 * 같은 골격을 쓰게 되면서 셸만 승격했다 — 각자 만들었더니 폴더 쪽이 한 벌짜리 가로 배치가
 * 되어 모바일에서 이미지가 카드 안에 남았다.
 *
 * `forwardRef` 는 퀴즈가 스크롤 위치 관찰(`heroCardRef`)에 쓴다.
 */
export const DetailHeroCard = forwardRef<HTMLDivElement, DetailHeroCardProps>(({
  image,
  mobileImage,
  mobileImageAside,
  children,
  className
}, ref) => {
  return <div ref={ref}>
        <div className={cn('flex w-full flex-col gap-2xs tablet-sm:hidden', className)}>
          <div className={'relative flex w-full items-end justify-between'}>
            {mobileImage ?? image}
            {mobileImageAside}
          </div>

          <div className={'flex w-full flex-col gap-sm rounded-md border border-opacity-assistive bg-white p-md shadow-md'}>
            {children}
          </div>
        </div>

        <div className={cn('hidden w-full items-start gap-xl rounded-md border border-opacity-assistive bg-white px-xl py-lg shadow-md tablet-sm:flex', className)}>
          {image}
          {children}
        </div>
      </div>;
});
DetailHeroCard.displayName = 'DetailHeroCard';